import { AxiosError, AxiosInstance } from "axios";
import { NavigateFunction } from "react-router-dom";
import { abortableWait } from "../../../../utils/abortableWait";
import { createApi } from "../../../../utils/createApi";
import { universalAPIErrorHandler } from "../../../../utils/universalAPIErrorHandler";
import { getNumbersAfterAboutSite } from "../SiteAbout";
import {
  ActionStatusResponse,
  CONFIGURE_MOTION_POLL_ATTEMPTS,
  CONFIGURE_MOTION_POLL_INTERVAL_MS,
  DISPATCH_CLOCK_SKEW_MS,
  PENDING_MESSAGE,
  REQUEST_FAILED_MESSAGE,
  TIMEOUT_MESSAGE,
  skippedMessage,
  summarizeConfigureMotion,
} from "./configureMotionStatus";
import {
  AlertState,
  ConfigureMotionState,
  Dispatcher,
} from "./siteAboutInterfaces";

export interface PollOptions {
  attempts?: number;
  intervalMs?: number;
  // Aborting stops the poll at the next step and suppresses any further state
  // updates — the caller aborts on unmount (and on a re-click) so a poll never
  // outlives the page it was started from.
  signal?: AbortSignal;
}

const setConfigureMotion = (
  setAlertState: Dispatcher<AlertState>,
  configureMotion: ConfigureMotionState
) => {
  setAlertState((previousState: AlertState) => {
    return { ...previousState, configureMotion };
  });
};

// Polls action_status until the Command the POST created reports an outcome.
// `dispatchedAt` filters out a row from an earlier click; a 404 (row not
// visible yet) or a transient error is retried like a pending row. Returns
// null, without touching state, if the signal aborts first.
export const pollConfigureMotionStatus = async (
  AxiosUI: AxiosInstance,
  siteId: string,
  setAlertState: Dispatcher<AlertState>,
  dispatchedAt: number,
  options: PollOptions = {}
): Promise<ConfigureMotionState | null> => {
  const attempts = options.attempts ?? CONFIGURE_MOTION_POLL_ATTEMPTS;
  const intervalMs = options.intervalMs ?? CONFIGURE_MOTION_POLL_INTERVAL_MS;
  const signal = options.signal;

  for (let attempt = 0; attempt < attempts; attempt++) {
    await abortableWait(intervalMs, signal);
    if (signal?.aborted) {
      return null;
    }
    try {
      const response = await AxiosUI.get<ActionStatusResponse>(
        `customer/${siteId}/action_status/?action=configure_motion`,
        { signal }
      );
      if (signal?.aborted) {
        return null;
      }
      const data = response.data;
      const fromThisClick =
        Date.parse(data.request_date) >= dispatchedAt - DISPATCH_CLOCK_SKEW_MS;
      if (fromThisClick && data.status && data.status !== "pending") {
        const state = summarizeConfigureMotion(data);
        setConfigureMotion(setAlertState, state);
        return state;
      }
    } catch {
      // 404 (no row yet), transient error, or the request was aborted: the
      // abort check at the top of the loop decides which.
    }
  }

  if (signal?.aborted) {
    return null;
  }
  const state: ConfigureMotionState = {
    phase: "timeout",
    message: TIMEOUT_MESSAGE,
  };
  setConfigureMotion(setAlertState, state);
  return state;
};

export const handleConfigureMotion = (
  setBackdropLoadingScreenOn: Dispatcher<boolean>,
  setAlertState: Dispatcher<AlertState>,
  navigate: NavigateFunction,
  pollOptions: PollOptions = {}
) => {
  const AxiosUI = createApi(``);
  const siteId = getNumbersAfterAboutSite(location.href);
  const dispatchedAt = Date.now();

  const retryFunction = () => {
    handleConfigureMotion(
      setBackdropLoadingScreenOn,
      setAlertState,
      navigate,
      pollOptions
    );
  };

  AxiosUI.post(`customer/${siteId}/action/configure_motion/`).then(
    (response) => {
      if (pollOptions.signal?.aborted) {
        // The page is gone; the job still runs and lands in Command History.
        return;
      }
      // The request is accepted; the outcome comes from the background job.
      // Don't hold the page behind the backdrop while it runs.
      setBackdropLoadingScreenOn(false);
      const skipped = skippedMessage(response?.data);
      if (skipped) {
        setConfigureMotion(setAlertState, {
          phase: "skipped",
          message: skipped,
        });
        return;
      }
      setConfigureMotion(setAlertState, {
        phase: "pending",
        message: PENDING_MESSAGE,
      });
      void pollConfigureMotionStatus(
        AxiosUI,
        siteId,
        setAlertState,
        dispatchedAt,
        pollOptions
      );
    },
    (reason: AxiosError) => {
      universalAPIErrorHandler(reason, navigate, retryFunction, () => {
        if (pollOptions.signal?.aborted) {
          return;
        }
        setConfigureMotion(setAlertState, {
          phase: "failed",
          message: REQUEST_FAILED_MESSAGE,
        });
        setBackdropLoadingScreenOn(false);
      });
    }
  );
};
